import React, { useState, useRef, useEffect } from 'react';
import { Form, Button } from 'react-bootstrap';
import { Edit2, Check, X } from 'lucide-react';

interface EditableDateFieldProps {
    value: string; // YYYY-MM-DD format
    onSave: (newValue: string) => Promise<void>;
    label?: string;
    icon?: React.ReactNode;
    required?: boolean;
    disabled?: boolean;
    min?: string; // YYYY-MM-DD format
    max?: string; // YYYY-MM-DD format
    displayStyle?: React.CSSProperties;
    showEditButton?: boolean;
}

export const EditableDateField: React.FC<EditableDateFieldProps> = ({
    value,
    onSave,
    label,
    icon,
    required = false,
    disabled = false,
    min,
    max,
    displayStyle = {},
    showEditButton = true,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);
    const [saving, setSaving] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // Update local state when value prop changes
    useEffect(() => {
        setEditValue(value);
    }, [value]);

    // Focus input when entering edit mode
    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isEditing]);

    const handleSave = async () => {
        if (required && !editValue) return;
        if (editValue === value) {
            setIsEditing(false);
            return;
        }

        setSaving(true);
        try {
            await onSave(editValue);
            setIsEditing(false);
        } catch (err) {
            // Error handling is done in the parent component
            console.error('Error saving:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setEditValue(value);
        setIsEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSave();
        } else if (e.key === 'Escape') {
            handleCancel();
        }
    };

    // Format date for display (YYYY-MM-DD to Swedish format)
    const formatDisplayDate = (dateStr: string): string => {
        if (!dateStr) return '';
        return dateStr; // Keep YYYY-MM-DD format for consistency
    };

    if (isEditing) {
        return (
            <div className="editable-date-field editing">
                {label && (
                <div className="editable-date-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#000', fontSize: '14px', fontWeight: 700, marginBottom: '4px' }}>
                    {icon}{label}
                </div>
            )}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Form.Control
                        type="date"
                        ref={inputRef}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={saving}
                        min={min}
                        max={max}
                        style={{ width: 'auto' }}
                    />
                    <Button
                        size="sm"
                        variant="success"
                        onClick={handleSave}
                        disabled={saving || (required && !editValue)}
                    >
                        <Check size={16} />
                    </Button>
                    <Button
                        size="sm"
                        variant="secondary"
                        onClick={handleCancel}
                        disabled={saving}
                    >
                        <X size={16} />
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="editable-date-field display">
            {label && (
                <div className="editable-date-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#000', fontSize: '14px', fontWeight: 700 }}>
                    {icon}{label}
                </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '1px', paddingLeft: icon ? '20px' : undefined }}>
                <span style={{ fontSize: '16px', ...displayStyle }}>
                    {formatDisplayDate(value) || <span style={{ color: '#999', fontStyle: 'italic' }}>Ej angivet</span>}
                </span>
                {showEditButton && !disabled && (
                    <Button
                        size="sm"
                        variant="link"
                        onClick={() => setIsEditing(true)}
                        style={{ padding: '2px 6px', color: '#666' }}
                    >
                        <Edit2 size={14} />
                    </Button>
                )}
            </div>
        </div>
    );
};
